import { useEffect, useState } from "react";
import { supplierTargets } from "@/api";
import { PILLARS } from "@/constants";
import { Target, Save } from "lucide-react";

/**
 * Panel de objetivos individuales del proveedor.
 * Permite a admin/evaluator capturar la meta de score global (0-10) y, opcionalmente,
 * la meta por pilar. Estos objetivos se muestran al proveedor junto al benchmark.
 */
export default function SupplierTargetsPanel({ supplierId, canEdit }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!supplierId) return;
    supplierTargets.get(supplierId)
      .then(setData)
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [supplierId]);

  const onSave = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      const payload = {
        score_target: data.score_target === "" || data.score_target == null ? null : Number(data.score_target),
        pillar_targets: Object.fromEntries(
          Object.entries(data.pillar_targets || {})
            .filter(([, v]) => v !== "" && v != null)
            .map(([k, v]) => [k, Number(v)])
        ),
        notes: data.notes || "",
      };
      const saved = await supplierTargets.set(supplierId, payload);
      setData(saved);
      setMsg("Objetivos guardados. Visibles al proveedor en su portal.");
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (!data) return null;

  return (
    <section className="sketch-border bg-[var(--cream-soft)] p-6" data-testid="supplier-targets-panel">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)] flex items-center gap-2">
            <Target size={18} /> objetivos acordados con el proveedor
          </div>
          <h2 className="font-display text-2xl">Meta individual de desempeño</h2>
          <p className="text-sm text-[var(--ink-soft)] max-w-2xl mt-1">
            Define la meta del proveedor para este ciclo. Se mostrará junto al promedio de su categoría en su portal,
            como referencia individual de crecimiento (no de comparación).
          </p>
        </div>
        {canEdit ? (
          <button onClick={onSave} disabled={busy} className="btn-primary text-sm" data-testid="save-targets-btn">
            <Save size={14} /> {busy ? "Guardando…" : "Guardar metas"}
          </button>
        ) : (
          <span className="text-[11px] font-bold uppercase tracking-wider text-[var(--ink-soft)] bg-[var(--cream-deep)] border border-[var(--line)] rounded-full px-3 py-1.5"
                data-testid="targets-readonly-note">
            Solo lectura — la edición es para Admin y Evaluadores
          </span>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-white border border-[var(--line)] rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--lala-blue)] font-bold mb-1">
            Meta de score global (0–10)
          </div>
          <input
            type="number" min="0" max="10" step="0.1"
            value={data.score_target ?? ""}
            onChange={(e) => setData({ ...data, score_target: e.target.value })}
            disabled={!canEdit}
            placeholder="Ej. 9.0"
            className="input-field"
            data-testid="target-score-input"
          />
          {data.score_target != null && data.score_target !== "" && (
            <p className="text-[11px] text-[var(--ink-soft)] mt-1">
              Equivale a <b>{(Number(data.score_target) * 10).toFixed(0)} /100</b>
            </p>
          )}
        </div>
        <div className="bg-white border border-[var(--line)] rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-[var(--lala-blue)] font-bold mb-1">
            Comentario interno (opcional)
          </div>
          <textarea
            value={data.notes || ""}
            onChange={(e) => setData({ ...data, notes: e.target.value })}
            disabled={!canEdit}
            placeholder="Acuerdo con el proveedor, condiciones, etc."
            rows={2}
            className="input-field"
          />
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-wider text-[var(--lala-blue)] font-bold mb-2">
          Meta por pilar (opcional · 0–10)
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {PILLARS.map((p) => (
            <div key={p.key} className="bg-white border border-[var(--line)] rounded-xl p-3" data-testid={`target-pillar-${p.key}`}>
              <div className="text-xs font-semibold text-[var(--ink)]">{p.label}</div>
              <input
                type="number" min="0" max="10" step="0.1"
                value={(data.pillar_targets || {})[p.key] ?? ""}
                onChange={(e) => setData({
                  ...data,
                  pillar_targets: { ...(data.pillar_targets || {}), [p.key]: e.target.value }
                })}
                disabled={!canEdit}
                placeholder="—"
                className="input-field mt-1 text-sm"
                data-testid={`target-pillar-input-${p.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {msg && <div className="mt-3 text-sm text-green-700">{msg}</div>}
      {err && <div className="mt-3 text-sm text-red-700">{err}</div>}
      {data.updated_at && (
        <p className="text-[10px] text-[var(--ink-soft)] mt-3">
          Última actualización: {new Date(data.updated_at).toLocaleString("es-MX")} {data.updated_by ? `· ${data.updated_by}` : ""}
        </p>
      )}
    </section>
  );
}
